import { useState, useRef, useEffect } from 'react';
import {
    Box,
    Grid,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Divider,
    TextField,
    InputAdornment,
} from '@mui/material';
import type { GridSize } from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SttList, SttPagination, SttLabelPeriod, SttNoResults } from '../all';
import useGroupsService from '../../services/GroupsService';
import { isDefined } from '../../lib/utils';
import SttError from '../../components/error/SttError';
import { Group } from '../../types/api/models';
import { sporttiaDimensions } from '../../theme/theme';

type SelectGroupPopupProps = {
    open?: boolean;
    onClose: () => void;
    onSelect: (group: Group) => void;
    types?: string;
    onlyActive?: boolean;
    noPagination?: boolean;
    excludeGroupId?: number;
};

/**
 * Select group popup dialog
 */
export function SelectGroupPopup({
    open = false,
    onClose,
    onSelect,
    types = '',
    onlyActive = false,
    noPagination = true,
    excludeGroupId,
}: SelectGroupPopupProps) {
    const { t } = useTranslation();
    const [searchForm, setSearchForm] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [getGroupsPage, setGetGroupsPage] = useState(1);
    const [getGroupsFilters, setGetGroupsFilters] = useState({
        name: '',
    });

    const groupsService = useGroupsService();
    const getGroupsQuery = groupsService.useGetGroups({
        ...(types ? { types } : {}),
        ...(noPagination ? {} : { page: getGroupsPage }),
        rows: noPagination ? 1000 : 7,
        includeChildren: true,
        ...(onlyActive ? { onlyActive } : {}),
        inTime: true,
        inFuture: true,
        ...(noPagination ? {} : getGroupsFilters),
    });

    // Focus search input when dialog opens
    useEffect(() => {
        if (open && searchInputRef.current) {
            // Small delay to ensure dialog is fully rendered
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [open]);

    // Handler to ensure page is reset to the first page when the filter is changed
    const handleOnFilter = ({
        name,
        value,
    }: {
        name: string;
        value: string;
    }) => {
        setGetGroupsPage(1);
        setGetGroupsFilters((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Filter groups locally when noPagination is enabled
    let filteredGroups =
        noPagination && getGroupsQuery.data?.rows
            ? getGroupsQuery.data.rows.filter((group: any) =>
                  group.name?.toLowerCase().includes(searchForm.toLowerCase()),
              )
            : getGroupsQuery.data?.rows || [];

    // Filter out excluded group
    if (excludeGroupId) {
        filteredGroups = filteredGroups.filter(
            (group: any) => group.id !== excludeGroupId,
        );
    }

    if (!open) {
        return null;
    }

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, minHeight: '60vh', maxHeight: '80vh' },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="span" fontWeight={600}>
                    {t('labels.selectGroup')}
                </Typography>
                <TextField
                    inputRef={searchInputRef}
                    size="small"
                    placeholder={t('common.search')}
                    value={searchForm}
                    onChange={(e: any) => setSearchForm(e.target.value)}
                    onKeyDown={(e: any) => {
                        if (e.key === 'Enter' && !noPagination) {
                            handleOnFilter({
                                name: 'name',
                                value: searchForm,
                            });
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{
                        flex: 1,
                        maxWidth: sporttiaDimensions.searchInput.maxWidth,
                    }}
                />
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {getGroupsQuery.isError && (
                    <Box paddingY={12}>
                        <SttError />
                    </Box>
                )}

                {getGroupsQuery.isLoading && (
                    <Box display="flex" justifyContent="center" paddingY={12}>
                        <CircularProgress />
                    </Box>
                )}

                {getGroupsQuery.isSuccess && filteredGroups.length > 0 && (
                    <SttList
                        onSelect={(group: Group) => onSelect(group)}
                        data={filteredGroups.map((group: any) => ({
                            caption: (
                                <Grid container alignItems="center">
                                    <Grid size={{ xs: 5 }}>
                                        <Box>
                                            {group.name}
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="div"
                                            >
                                                {isDefined(
                                                    group.enrollmentPrice,
                                                ) && (
                                                    <span>
                                                        {t('Enrollment')}:{' '}
                                                        {group.enrollmentPrice}€
                                                    </span>
                                                )}
                                                {isDefined(
                                                    group.enrollmentPrice,
                                                ) &&
                                                    isDefined(group.feePrice) &&
                                                    ' | '}
                                                {isDefined(group.feePrice) && (
                                                    <span>
                                                        {t('Fee')}:{' '}
                                                        {group.feePrice}€
                                                    </span>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 3 }}>
                                        {isDefined(group.numActiveUsers) && (
                                            <Chip
                                                label={`${group.numActiveUsers} ${t('labels.members')}`}
                                                size="small"
                                            />
                                        )}
                                    </Grid>
                                    <Grid
                                        size={{ xs: 4 }}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <SttLabelPeriod
                                            ini={group.ini}
                                            end={group.end}
                                        />
                                    </Grid>
                                </Grid>
                            ),
                            text: group.description,
                            objToSelect: group,
                        }))}
                    />
                )}

                {getGroupsQuery.isSuccess && filteredGroups.length === 0 && (
                    <Box paddingY={12}>
                        <SttNoResults />
                    </Box>
                )}

                {!noPagination &&
                    getGroupsQuery.isSuccess &&
                    getGroupsQuery.data.count > 0 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <SttPagination
                                page={getGroupsPage}
                                pages={getGroupsQuery.data.pages}
                                count={getGroupsQuery.data.count}
                                onChangePage={(page) => {
                                    setGetGroupsPage(page);
                                }}
                            />
                        </Box>
                    )}
            </DialogContent>
        </Dialog>
    );
}

/**
 * SelectGroupField - TextField with search/clear icons that opens SelectGroupPopup
 */
type SelectGroupFieldProps = {
    grid?: boolean;
    caption: string;
    name: string;
    defVal:
        | {
              id: number;
              name: string;
          }
        | null
        | undefined;
    onChange: ({ name, value }: { name: string; value: Group | null }) => void;
    onlyActive?: boolean;
    types?: string;
    excludeGroupId?: number;
    xs?: GridSize;
    sm?: GridSize;
    md?: GridSize;
    lg?: GridSize;
    xl?: GridSize;
};

export function SelectGroupField({
    grid = false,
    caption,
    name,
    defVal,
    onChange,
    md = 3,
    xs = 12,
    sm,
    lg,
    xl,
    onlyActive = false,
    types,
    excludeGroupId,
}: SelectGroupFieldProps) {
    const { t } = useTranslation();

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const noGroup = {
        id: 0,
        name: t('generic.noGroup'),
    };

    const defaultValue = defVal ?? noGroup;

    const content = (
        <>
            {isDialogOpen && (
                <SelectGroupPopup
                    open
                    onClose={() => setIsDialogOpen(false)}
                    onSelect={(group: Group) => {
                        setIsDialogOpen(false);
                        onChange({ name, value: group });
                    }}
                    onlyActive={onlyActive}
                    types={types}
                    excludeGroupId={excludeGroupId}
                />
            )}
            <TextField
                fullWidth
                size="small"
                label={caption}
                variant="outlined"
                value={defaultValue.name}
                slotProps={{
                    input: {
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => {
                                        onChange({
                                            name,
                                            value: null,
                                        });
                                    }}
                                >
                                    <ClearIcon />
                                </IconButton>

                                <IconButton
                                    onClick={() => {
                                        setIsDialogOpen(true);
                                    }}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        </>
    );

    return grid ? (
        <Grid size={{ xs, sm, md, lg, xl }}>{content}</Grid>
    ) : (
        <div>{content}</div>
    );
}
